<template lang="pug">
.modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="props.showModal" @click="closeModal")
  .modal-content.max-w-6xl.max-h-95vh.bg-white.dark_bg-gray-800.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
    //- Header
    .flex.justify-between.items-center.p-6.border-b.border-gray-200.dark_border-gray-600
      h3.text-xl.font-bold.text-gray-800.dark_text-gray-200
        | Generate Thumbnails for Course {{ courseId }}
      button.text-gray-500.hover_text-gray-700.dark_text-gray-400.dark_hover_text-gray-200(
        @click="closeModal"
        :disabled="isGenerating"
      )
        svg.w-6.h-6(fill="none" stroke="currentColor" viewBox="0 0 24 24")
          path(stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12")
    
    //- Content
    .p-6.flex.flex-col.h-full
      //- Thumbnail Types Section (above preview)
      .thumbnail-types-section.mb-6
        .flex.justify-between.items-center.mb-4
          h4.text-lg.font-semibold.text-gray-800.dark_text-gray-200 Thumbnail Types
          button.btn.bg-gray-100.dark_bg-gray-600.text-gray-600.dark_text-gray-300.px-3.py-1.text-sm.rounded.hover_bg-gray-200.dark_hover_bg-gray-500(
            @click="selectAllTypes"
            :disabled="isGenerating"
            :class="{ 'opacity-50 cursor-not-allowed': isGenerating }"
          ) Select All
        p.italic.text-gray-500.dark_text-gray-400.mb-4.mt-0 Select at least one thumbnail type to generate for preview and upload.
        
        //- Delay Configuration
        .delay-config.mb-4.p-4.bg-gray-50.dark_bg-gray-700.rounded-lg.border.border-gray-200.dark_border-gray-600.text-center
          h5.text-md.font-medium.mb-2.text-gray-700.dark_text-gray-300 Screenshot Delay
          p.text-sm.text-gray-500.dark_text-gray-400.mb-3
            | How long to wait before taking the screenshot (in seconds). Increase if thumbnails show loading screens.
          .flex.items-center.justify-center.gap-4
            input.w-20.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded.bg-white.dark_bg-gray-800.text-gray-800.dark_text-gray-200(
              type="number"
              v-model="screenshotDelay"
              min="5"
              max="30"
              step="1"
            )
            span.text-sm.text-gray-600.dark_text-gray-400 seconds
            button.btn.bg-gray-200.dark_bg-gray-600.text-gray-700.dark_text-gray-300.px-3.py-2.text-sm.rounded(
              @click="resetDelay"
            ) Reset to Default
        
        //- Thumbnail Type Cards in One Row
        .grid.grid-cols-4.gap-4.mb-6
          .thumbnail-type.border.border-gray-300.dark_border-gray-600.rounded-lg.p-4.bg-white.dark_bg-gray-700.cursor-pointer(
            v-for="type in thumbnailTypes" 
            :key="type.name"
            @click="toggleTypeSelection(type.name)"
            :class="{ 'opacity-50 cursor-not-allowed': generationStatus[type.name] === 'completed' }"
          )
            .text-center
              //- Checkbox at the top
              .mb-3
                input.w-4.h-4.text-primary-600.bg-gray-100.border-gray-300.rounded.focus_ring-primary-500.dark_focus_ring-primary-500.dark_ring-offset-gray-700.focus_ring-2.dark_bg-gray-600.dark_border-gray-500(
                  type="checkbox"
                  :id="`checkbox-${type.name}`"
                  :value="type.name"
                  v-model="selectedTypes"
                  :disabled="generationStatus[type.name] === 'completed'"
                  @click.stop
                )
              
              .font-medium.text-gray-800.dark_text-gray-200.mb-1 {{ type.label }}
              .text-sm.text-gray-500.dark_text-gray-400.mb-3 ({{ type.width }}x{{ type.height }})
              
              //- Status indicator
              .text-sm(
                :class="getStatusClass(generationStatus[type.name])"
              )
                span(v-if="generationStatus[type.name] === 'pending'") Ready to generate
                span(v-else-if="generationStatus[type.name] === 'generating'") Generating...
                span(v-else-if="generationStatus[type.name] === 'completed'") ✅ Generated
                span(v-else-if="generationStatus[type.name] === 'error'") ❌ Failed
          
        //- Action Buttons
        .flex.gap-4.justify-center
          button.btn.bg-primary-600.dark_bg-primary-600.text-white.py-3.px-8.text-lg(
              @click="generateSelectedThumbnails"
            :disabled="isGenerating || !canGenerateMore"
            :class="{ 'opacity-50 cursor-not-allowed': isGenerating || !canGenerateMore }"
            )
              span {{ generateButtonText }}
            
          button.btn.bg-blue-600.text-white.py-3.px-8.text-lg(
              v-if="allSelectedThumbnailsCompleted"
            @click="closeAndPreview"
            :class="{ 'opacity-50 cursor-not-allowed': false }"
            )
            span Close and Preview Thumbnails
          
          //- Progress Indicator
        .mt-4(v-if="isGenerating")
          .text-sm.text-gray-600.dark_text-gray-400.mb-2.text-center Progress
          .w-full.bg-gray-200.dark_bg-gray-600.rounded-full.h-3
            .bg-blue-600.h-3.rounded-full.transition-all.duration-300(
                :style="{ width: `${generationProgress}%` }"
              )
          .text-sm.text-gray-500.dark_text-gray-400.mt-2.text-center {{ selectedGeneratedCount }}/{{ selectedTypes.length }} completed
      

</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Icon } from '@iconify/vue';
import {
  uploadThumbnail,
  generateThumbnail as generateThumbnailAPI
} from '../lib/callModContentAuthors';

interface GeneratedThumbnail {
  type: string;
  filename: string;
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

const props = defineProps<{
  courseId: number;
  firmCode?: string;
  showModal: boolean;
}>();

const emit = defineEmits<{
  close: [];
  'thumbnails-generated': [thumbnails: GeneratedThumbnail[]];
}>();

// Generation state
const isGenerating = ref(false);

// Thumbnail types configuration
const thumbnailTypes = [
  { name: 'standard', label: 'Standard', width: 1366, height: 768 },
  { name: 'hero', label: 'Hero', width: 2080, height: 694 },
  { name: 'tile', label: 'Tile', width: 1200, height: 888 },
  { name: 'noTitle', label: 'No Title', width: 1366, height: 768 }
];

// Helper functions for environment detection and URL building
const resolvePortalBase = () => {
  const hostname = window.location.hostname;

  if (
    hostname.includes('-dev') ||
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1')
  ) {
    return 'portal.vinciworks-dev.com';
  } else if (hostname.includes('-qa')) {
    return 'portal.vinciworks-qa.com';
  } else {
    return 'portal.vinciworks.com';
  }
};

const buildCourseUrl = (courseId: number, thumbnailType: string) => {
  const base = resolvePortalBase();
  return `https://${base}/awscontent/ubercourse/latest/app.html?courseid=${courseId}&thumbnail=${thumbnailType}#/welcome`;
};

const typeToViewport = (type: string) => {
  const config = thumbnailTypes.find(t => t.name === type);
  return {
    width: config?.width || 1366,
    height: config?.height || 768
  };
};

// Generation status tracking
const generationStatus = ref<
  Record<string, 'pending' | 'generating' | 'completed' | 'error'>
>({
  standard: 'pending',
  hero: 'pending',
  tile: 'pending',
  noTitle: 'pending'
});

// Generated thumbnails storage
const generatedThumbnails = ref<GeneratedThumbnail[]>([]);

// Selection state for thumbnail types
const selectedTypes = ref<string[]>([]);

// Screenshot delay configuration
const screenshotDelay = ref<number>(8); // Default 12 seconds

// Computed properties
const generatedCount = computed(
  () =>
    Object.values(generationStatus.value).filter(
      status => status === 'completed'
    ).length
);

const selectedGeneratedCount = computed(() => {
  return selectedTypes.value.filter(
    type => generationStatus.value[type] === 'completed'
  ).length;
});

const generationProgress = computed(() => {
  if (selectedTypes.value.length === 0) return 0;
  return (selectedGeneratedCount.value / selectedTypes.value.length) * 100;
});

const allThumbnailsGenerated = computed(
  () => generatedCount.value === thumbnailTypes.length
);

const generateButtonText = computed(() => {
  if (isGenerating.value) return 'Generating Thumbnails...';
  if (selectedTypes.value.length === 0) return 'Generate Thumbnails';

  const selectedCompleted = selectedTypes.value.filter(
    type => generationStatus.value[type] === 'completed'
  ).length;

  if (selectedCompleted === 0) return 'Generate Thumbnails';
  if (selectedCompleted === selectedTypes.value.length)
    return 'Generate Additional Thumbnails';
  return 'Generate More Thumbnails';
});

const canGenerateMore = computed(() => {
  if (selectedTypes.value.length === 0) return false;
  return selectedTypes.value.some(
    type => generationStatus.value[type] !== 'completed'
  );
});

const anyThumbnailGenerated = computed(() => generatedCount.value > 0);

const allSelectedThumbnailsCompleted = computed(() => {
  if (selectedTypes.value.length === 0) return false;
  return selectedTypes.value.every(
    type => generationStatus.value[type] === 'completed'
  );
});

// Methods
const closeModal = () => {
  if (!isGenerating.value) {
    emit('close');
  }
};

const toggleTypeSelection = (typeName: string) => {
  // Don't allow selection if already completed
  if (generationStatus.value[typeName] === 'completed') {
    return;
  }

  const index = selectedTypes.value.indexOf(typeName);
  if (index > -1) {
    selectedTypes.value.splice(index, 1);
  } else {
    selectedTypes.value.push(typeName);
  }
};

const closeAndPreview = () => {
  // Emit the generated thumbnails before closing
  emit('thumbnails-generated', generatedThumbnails.value);
  closeModal();
};

const resetDelay = () => {
  screenshotDelay.value = 12; // Reset to default 12 seconds
};

const selectAllTypes = () => {
  if (isGenerating.value) return;

  // Select all types that are not already completed
  const availableTypes = thumbnailTypes
    .map(t => t.name)
    .filter(typeName => generationStatus.value[typeName] !== 'completed');

  selectedTypes.value = [...availableTypes];
};

const generateThumbnail = async (thumbnailType: string) => {
  try {
    generationStatus.value[thumbnailType] = 'generating';

    console.log(
      `Generating ${thumbnailType} thumbnail for course ${props.courseId} with ${screenshotDelay.value}s delay`
    );

    // Call the backend proxy with custom delay
    const response = await generateThumbnailAPI(
      props.courseId,
      thumbnailType,
      screenshotDelay.value
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate thumbnail');
    }

    // Convert base64 dataUrl to Blob for consistency with existing code
    const base64Data = response.dataUrl.split(',')[1];
    const binaryData = atob(base64Data);
    const arrayBuffer = new ArrayBuffer(binaryData.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < binaryData.length; i++) {
      uint8Array[i] = binaryData.charCodeAt(i);
    }

    const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });

    // Create thumbnail object
    const thumbnailData: GeneratedThumbnail = {
      type: thumbnailType,
      filename: response.filename,
      blob,
      dataUrl: response.dataUrl,
      width: response.width,
      height: response.height
    };

    // Store the generated thumbnail
    generatedThumbnails.value.push(thumbnailData);
    generationStatus.value[thumbnailType] = 'completed';

    console.log(
      `✅ Successfully generated ${thumbnailType} thumbnail:`,
      thumbnailData
    );
  } catch (error) {
    console.error(`Error generating ${thumbnailType} thumbnail:`, error);
    generationStatus.value[thumbnailType] = 'error';

    // Show user-friendly error message
    if (error instanceof Error) {
      console.error(`Detailed error: ${error.message}`);
    }
  }
};

const generateSelectedThumbnails = async () => {
  isGenerating.value = true;

  try {
    for (const thumbnailType of selectedTypes.value) {
      if (generationStatus.value[thumbnailType] !== 'completed') {
        await generateThumbnail(thumbnailType);
      }
    }
  } finally {
    isGenerating.value = false;
  }
};

const generateAllThumbnails = async () => {
  isGenerating.value = true;

  try {
    for (const thumbnailType of thumbnailTypes) {
      if (generationStatus.value[thumbnailType.name] !== 'completed') {
        await generateThumbnail(thumbnailType.name);
      }
    }
  } finally {
    isGenerating.value = false;
  }
};

const getStatusClass = (status: string) => {
  switch (status) {
    case 'pending':
      return 'text-gray-500 dark_text-gray-400';
    case 'generating':
      return 'text-blue-600 dark_text-blue-400';
    case 'completed':
      return 'text-green-600 dark_text-green-400';
    case 'error':
      return 'text-red-600 dark_text-red-400';
    default:
      return 'text-gray-500 dark_text-gray-400';
  }
};
</script>

<style scoped>
.modal-overlay {
  backdrop-filter: blur(2px);
}

.modal-content {
  max-height: 95vh;
  overflow-y: auto;
  width: 95vw;
  max-width: 1200px;
}

.preview-container {
  min-height: 400px;
}

.btn {
  @apply px-3 py-2 rounded font-medium transition-colors duration-200;
}

.btn-sm {
  @apply px-2 py-1 text-sm;
}

.btn:disabled {
  @apply opacity-50 cursor-not-allowed;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .modal-content {
    width: 98vw;
    max-width: none;
  }

  .grid.grid-cols-4 {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .grid.grid-cols-4 {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
